import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button, Card, Form, Input, Select, Radio, message, Row, Col, Space } from 'antd';
import { ArrowRightOutlined, CustomerServiceOutlined, PlusOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { ticketsApi, show422Error } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';

const { Option } = Select;
const { TextArea } = Input;

const priorityOptions = [
  { value: 'low', label: 'پایین' },
  { value: 'medium', label: 'متوسط' },
  { value: 'high', label: 'بالا' },
  { value: 'urgent', label: 'فوری' },
];

const agentsForGroup = (groups, groupId) => {
  if (groupId == null || groupId === '') return [];
  const g = (groups || []).find((x) => String(x.id) === String(groupId));
  return (g?.enabled_agents || [])
    .map((ea) => ({
      id: ea.agent_id ?? ea.user?.id,
      name: ea.user?.name,
    }))
    .filter((a) => a.id != null);
};

const AddTicket = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const presetProjectId =
    searchParams.get('project') ||
    searchParams.get('project_id') ||
    location.state?.projectId;
  const returnTo = location.state?.returnTo || searchParams.get('returnTo');
  const lockProject = Boolean(presetProjectId) || Boolean(location.state?.lockProject);
  const { canAdd, permissionsRevision } = useResourcePermissions('tickets');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [options, setOptions] = useState({
    clients: [],
    employees: [],
    groups: [],
    projects: [],
    types: [],
    channels: [],
  });
  const requesterType = Form.useWatch('requester_type', form);
  const groupId = Form.useWatch('group_id', form);

  const agentChoices = useMemo(() => agentsForGroup(options.groups, groupId), [options.groups, groupId]);

  const goBack = () => navigate(returnTo || '/tickets');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await ticketsApi.getCreateData(
          presetProjectId != null && presetProjectId !== ''
            ? { project: presetProjectId }
            : {},
        );
        const data = res?.data || res || {};
        const selectedProject = data.selected_project;
        const projects = data.projects || [];
        // اگر selected_project در لیست پروژه‌ها نبود، اضافه کن تا Select درست نمایش دهد
        if (
          selectedProject?.id != null &&
          !projects.some((p) => String(p.id) === String(selectedProject.id))
        ) {
          projects.push({
            id: selectedProject.id,
            project_name: selectedProject.project_name,
          });
        }
        setOptions({
          clients: data.clients || [],
          employees: data.employees || [],
          groups: data.groups || [],
          projects,
          types: data.types || [],
          channels: data.channels || [],
        });

        const initial = {
          requester_type: 'client',
          priority: 'medium',
        };
        if (data.selected_client?.id != null) {
          initial.requester_type = 'client';
          initial.client_id = data.selected_client.id;
        }
        if (data.default_assign) {
          const da = data.default_assign;
          if (da.group_id != null) initial.group_id = da.group_id;
          if (da.agent_id != null) initial.agent_id = da.agent_id;
        }
        if (selectedProject?.id != null) {
          initial.project_id = Number(selectedProject.id);
        } else if (presetProjectId != null) {
          initial.project_id = Number(presetProjectId);
        }
        form.setFieldsValue(initial);
      } catch (error) {
        message.error('خطا در دریافت فرم ایجاد تیکت');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [form, presetProjectId]);

  useEffect(() => {
    if (loading) return;
    if (!canAdd) {
      message.warning('شما مجوز ایجاد تیکت ندارید');
      navigate(returnTo || '/tickets', { replace: true });
    }
  }, [loading, canAdd, permissionsRevision, navigate, returnTo]);

  useEffect(() => {
    if (groupId == null) return;
    const allowed = new Set(agentChoices.map((a) => String(a.id)));
    const current = form.getFieldValue('agent_id');
    if (current != null && !allowed.has(String(current))) {
      form.setFieldsValue({ agent_id: undefined });
    }
  }, [groupId, agentChoices, form]);

  const handleSubmit = async (values) => {
    // تبدیل آرایه تگ‌ها به JSON string
    const tagsArray = (values.tag_labels || [])
      .filter(Boolean)
      .map((v) => ({ value: v }));
    
    // تبدیل به JSON string
    const tagsJsonString = JSON.stringify(tagsArray);
    
    const payload = {
      requester_type: values.requester_type,
      group_id: values.group_id,
      agent_id: values.agent_id ?? null,
      project_id: values.project_id ?? null,
      type_id: values.type_id,
      subject: values.subject,
      description: values.description,
      priority: values.priority,
      channel_id: values.channel_id,
      tags: tagsJsonString,  // اینجا JSON string ارسال می‌شود نه آرایه
    };
    
    if (values.requester_type === 'client') {
      payload.client_id = values.client_id;
    } else {
      payload.user_id = values.user_id;
    }
  
    console.log('Payload:', payload);
    console.log('Tags as string:', tagsJsonString);
    // خروجی: [{"value":"تست 1"},{"value":"تست2"}]
  
    setSubmitLoading(true);
    try {
      await ticketsApi.create(payload);
      message.success('تیکت با موفقیت ایجاد شد');
      navigate(returnTo || '/tickets');
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ایجاد تیکت');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="custom-font" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-sky-50 dark:bg-sky-900/20 flex items-center justify-center">
                <CustomerServiceOutlined className="text-sky-600 dark:text-sky-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">تیکت جدید</h1>
                {/* <p className="text-gray-500 dark:text-gray-400 text-sm m-0">موضوع، گروه و سایر فیلدها را تکمیل کنید</p> */}
              </div>
            </div>
            <Space wrap>
              <Button icon={<ArrowRightOutlined />} onClick={goBack}>
                بازگشت
              </Button>
              <Button type="primary" icon={<PlusOutlined />} disabled className={PANEL_PRIMARY_BTN_CLASS}>
                راهنما
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {loading ? (
              <TableSkeleton rows={8} columns={2} />
            ) : (
              <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 custom-font">
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSubmit}
                  className="max-w-4xl custom-font"
                  initialValues={{ tag_labels: [] }}
                >
                <Form.Item name="requester_type" label="نوع درخواست‌کننده">
                  <Radio.Group>
                    <Radio value="client">مشتری</Radio>
                    <Radio value="employee">کارمند</Radio>
                  </Radio.Group>
                </Form.Item>

                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    {requesterType === 'client' ? (
                      <Form.Item name="client_id" label="مشتری">
                        <Select placeholder="انتخاب مشتری" showSearch optionFilterProp="children">
                          {(options.clients || []).map((c) => (
                            <Option key={c.id} value={c.id}>
                              {c.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    ) : (
                      <Form.Item name="user_id" label="کارمند">
                        <Select placeholder="انتخاب کارمند" showSearch optionFilterProp="children">
                          {(options.employees || []).map((e) => (
                            <Option key={e.id} value={e.id}>
                              {e.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="group_id" label="گروه">
                      <Select placeholder="گروه" allowClear={false}>
                        {(options.groups || []).map((g) => (
                          <Option key={g.id} value={g.id}>
                            {g.group_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item name="agent_id" label="پشتیبان اختصاصی">
                      <Select placeholder="انتخاب پشتیبان (اختیاری)" allowClear>
                        {agentChoices.map((a) => (
                          <Option key={a.id} value={a.id}>
                            {a.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="project_id" label="پروژه">
                      <Select
                        placeholder="بدون پروژه"
                        allowClear={!lockProject}
                        disabled={lockProject}
                      >
                        {(options.projects || []).map((p) => (
                          <Option key={p.id} value={p.id}>
                            {p.project_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item name="type_id" label="نوع تیکت">
                      <Select placeholder="نوع">
                        {(options.types || []).map((t) => (
                          <Option key={t.id} value={t.id}>
                            {t.type}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="channel_id" label="کانال">
                      <Select placeholder="کانال">
                        {(options.channels || []).map((c) => (
                          <Option key={c.id} value={c.id}>
                            {c.channel_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item name="priority" label="اولویت">
                      <Select>
                        {priorityOptions.map((p) => (
                          <Option key={p.value} value={p.value}>
                            {p.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="subject" label="موضوع">
                  <Input />
                </Form.Item>

                <Form.Item name="description" label="توضیحات">
                  <TextArea rows={5} />
                </Form.Item>

                <Form.Item name="tag_labels" label="تگ‌ها">
                  <Select mode="tags" placeholder="تگ اضافه کنید" tokenSeparators={[',']} />
                </Form.Item>

                  <Form.Item>
                    <Space>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={submitLoading}
                        className={PANEL_PRIMARY_BTN_CLASS}
                      >
                        ثبت تیکت
                      </Button>
                      <Button onClick={goBack}>انصراف</Button>
                    </Space>
                  </Form.Item>
                </Form>
              </Card>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AddTicket;
